import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { useAuthStore, isAdminRole, isMasterRole } from './store';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import { getWebApp } from './telegram';

import { HomePage } from './pages/HomePage';
import { ServicesPage } from './pages/ServicesPage';
import { BookWizardPage } from './pages/BookWizardPage';
import { MyBookingsPage } from './pages/MyBookingsPage';
import { BookingDetailsPage } from './pages/BookingDetailsPage';
import { ProfilePage } from './pages/ProfilePage';
import { WaitlistPage } from './pages/WaitlistPage';
import { BecomeMasterPage } from './pages/BecomeMasterPage';

import { MasterCalendarPage } from './pages/master/CalendarPage';
import { MasterBookingsPage } from './pages/master/BookingsPage';
import { MasterSchedulePage } from './pages/master/SchedulePage';
import { MasterSettingsPage } from './pages/master/SettingsPage';

import { AdminServicesPage } from './pages/admin/AdminServicesPage';
import { AdminMastersPage } from './pages/admin/AdminMastersPage';

export function App() {
  const status = useAuthStore((s) => s.status);
  const error = useAuthStore((s) => s.error);
  const init = useAuthStore((s) => s.init);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (status === 'idle') void init();
  }, [status, init]);

  // Telegram BackButton integration
  useEffect(() => {
    const wa = getWebApp();
    if (!wa) return;
    const onBack = () => navigate(-1);
    if (location.pathname === '/' || location.pathname === '') {
      wa.BackButton.hide();
    } else {
      wa.BackButton.show();
      wa.BackButton.onClick(onBack);
    }
    return () => {
      wa.BackButton.offClick(onBack);
    };
  }, [location.pathname, navigate]);

  if (status === 'loading' || status === 'idle') {
    return <SplashScreen text="Авторизация..." />;
  }
  if (status === 'error') {
    return <SplashScreen text={error ?? 'Не удалось войти'} retry />;
  }

  return (
    <ErrorBoundary>
      <Layout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/book" element={<BookWizardPage />} />
          <Route path="/book/:serviceId" element={<BookWizardPage />} />
          <Route path="/my-bookings" element={<MyBookingsPage />} />
          <Route path="/bookings/:id" element={<BookingDetailsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/waitlist" element={<WaitlistPage />} />
          <Route path="/become-master" element={<BecomeMasterPage />} />

          <Route path="/master" element={<MasterRoute />}>
            <Route index element={<Navigate to="/master/calendar" replace />} />
            <Route path="calendar" element={<MasterCalendarPage />} />
            <Route path="bookings" element={<MasterBookingsPage />} />
            <Route path="schedule" element={<MasterSchedulePage />} />
            <Route path="settings" element={<MasterSettingsPage />} />
          </Route>

          <Route path="/admin" element={<AdminRoute />}>
            <Route index element={<Navigate to="/admin/services" replace />} />
            <Route path="services" element={<AdminServicesPage />} />
            <Route path="masters" element={<AdminMastersPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </ErrorBoundary>
  );
}

function MasterRoute() {
  const role = useAuthStore((s) => s.user?.role);
  if (!isMasterRole(role)) return <Navigate to="/" replace />;
  return <Layout.Outlet />;
}

function AdminRoute() {
  const role = useAuthStore((s) => s.user?.role);
  if (!isAdminRole(role)) return <Navigate to="/" replace />;
  return <Layout.Outlet />;
}

function SplashScreen({ text, retry }: { text: string; retry?: boolean }) {
  const init = useAuthStore((s) => s.init);
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
      <div className="text-2xl">💅</div>
      <p className="text-tg-hint">{text}</p>
      {retry && (
        <button className="btn-primary" onClick={() => void init()}>
          Повторить
        </button>
      )}
    </div>
  );
}
